import { useState, useEffect } from 'react';
import enRes from '../i18n/en.json';
import zhTWRes from '../i18n/zh-TW.json';
import zhCNRes from '../i18n/zh-CN.json';

type Language = 'en' | 'zh-TW' | 'zh-CN';
type TranslationValue = string | Record<string, any>;
type Translations = Record<string, TranslationValue>;

const translations: Record<Language, Translations> = {
  'en': {},
  'zh-TW': {},
  'zh-CN': {}
};

let translationsLoaded = false;

const loadTranslations = () => {
  if (translationsLoaded) return;
  
  try {
    translations.en = enRes;
    translations['zh-TW'] = zhTWRes;
    translations['zh-CN'] = zhCNRes;
    
    translationsLoaded = true;
  } catch (error) {
    console.error('Failed to load translations:', error);
    // Fallback to empty objects if loading fails
    translationsLoaded = true;
  }
};

loadTranslations();

export const useTranslation = (language: string = 'en') => {
  // Normalize language code
  const normalizedLang = normalizeLanguage(language);

  const t = (key: string, params?: Record<string, string | number>): string => {
    // Support nested keys with dot notation
    const keys = key.split('.');
    let translation: any = translations[normalizedLang] || translations.en;
    
    // Navigate through nested objects
    for (const k of keys) {
      translation = translation?.[k];
      if (translation === undefined) {
        // Try fallback to English
        translation = translations.en;
        for (const k2 of keys) {
          translation = translation?.[k2];
          if (translation === undefined) break;
        }
        break;
      }
    }
    
    // If no translation found, return the key
    if (translation === undefined || typeof translation !== 'string') {
      return key;
    }
    
    // Replace parameters in the translation
    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        translation = translation.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value));
      });
    }
    
    return translation;
  };

  return { t };
};

const normalizeLanguage = (language: string): Language => {
  const lang = language.toLowerCase();
  
  if (lang.includes('zh')) {
    if (lang.includes('tw') || lang.includes('hk') || lang.includes('hant')) {
      return 'zh-TW';
    } else if (lang.includes('cn') || lang.includes('hans')) {
      return 'zh-CN';
    }
    // Default to Traditional Chinese for other Chinese variants
    return 'zh-TW';
  }
  
  return 'en';
};

export default useTranslation;
